import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';
import { testimonialsApi, formatApiError } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { toast } from 'sonner';
import { Star, RefreshCw, Search, Loader2, Check, EyeOff, Trash2 } from 'lucide-react';

const PAGE_SIZE = 20;

export default function AdminTestimonialsPage() {
    const { t } = useTranslation();
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(0);
    const [filter, setFilter] = useState('all');
    const [search, setSearch] = useState('');
    const [busyId, setBusyId] = useState(null);
    const [confirmDelete, setConfirmDelete] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const res = await testimonialsApi.adminList({
                skip: page * PAGE_SIZE,
                limit: PAGE_SIZE,
                published_filter: filter,
                search: search || undefined,
            });
            setItems(res.data.testimonials || []);
            setTotal(res.data.total || 0);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, filter, search]);

    const moderate = async (item, published) => {
        setBusyId(item.id);
        try {
            await testimonialsApi.adminModerate(item.id, published);
            toast.success(published ? t('testimonials_page.admin_published_success') : t('testimonials_page.admin_unpublished_success'));
            await load();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusyId(null);
        }
    };

    const handleDelete = async () => {
        if (!confirmDelete) return;
        setBusyId(confirmDelete.id);
        try {
            await testimonialsApi.adminDelete(confirmDelete.id);
            toast.success(t('testimonials_page.admin_deleted_success'));
            setConfirmDelete(null);
            await load();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusyId(null);
        }
    };

    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
    const locale = i18n.language === 'en' ? 'en-GB' : 'fr-FR';

    const emptyLabel = filter === 'pending' ? t('testimonials_page.admin_empty_pending')
        : filter === 'published' ? t('testimonials_page.admin_empty_published')
        : t('testimonials_page.admin_empty_default');

    return (
        <div className="space-y-6" data-testid="admin-testimonials-page">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-[#0A0A0A] font-['Outfit']">{t('testimonials_page.admin_title')}</h1>
                    <p className="text-sm text-[#9CA3AF] mt-1">
                        {t('testimonials_page.admin_subtitle')}
                    </p>
                </div>
                <Button variant="outline" onClick={load} disabled={loading} data-testid="testimonials-refresh-btn">
                    <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                    {t('common.refresh')}
                </Button>
            </div>

            {/* Filters */}
            <div className="bg-white border border-[#E5E7EB] rounded-lg p-4 flex flex-col md:flex-row gap-3">
                <div className="relative flex-1">
                    <Search className="w-4 h-4 absolute left-3 top-3 text-[#9CA3AF]" />
                    <Input
                        placeholder={t('testimonials_page.admin_search_placeholder')}
                        value={search}
                        onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                        className="pl-10"
                        data-testid="testimonials-search"
                    />
                </div>
                <Select value={filter} onValueChange={(v) => { setFilter(v); setPage(0); }}>
                    <SelectTrigger className="md:w-56" data-testid="testimonials-filter">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">{t('testimonials_page.admin_filter_all')}</SelectItem>
                        <SelectItem value="pending">{t('testimonials_page.admin_filter_pending')}</SelectItem>
                        <SelectItem value="published">{t('testimonials_page.admin_filter_published')}</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            {/* List */}
            <div className="bg-white border border-[#E5E7EB] rounded-lg divide-y divide-[#F3F4F6]">
                {loading ? (
                    <div className="p-12 text-center">
                        <Loader2 className="w-6 h-6 animate-spin mx-auto text-[#9CA3AF]" />
                    </div>
                ) : items.length === 0 ? (
                    <div className="p-12 text-center text-[#9CA3AF]" data-testid="testimonials-empty">
                        {emptyLabel}
                    </div>
                ) : items.map((tItem) => (
                    <div key={tItem.id} className="p-5" data-testid={`testimonial-row-${tItem.id}`}>
                        <div className="flex items-start justify-between gap-4">
                            <div className="flex-1">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className="font-semibold text-[#0A0A0A]">{tItem.author_name}</span>
                                    {tItem.author_title && (
                                        <span className="text-sm text-[#4B5563]">· {tItem.author_title}</span>
                                    )}
                                    {tItem.author_company && (
                                        <span className="text-sm text-[#9CA3AF]">· {tItem.author_company}</span>
                                    )}
                                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                        tItem.published ? 'bg-[#D1FAE5] text-[#059669]' : 'bg-[#FEF3C7] text-[#D97706]'
                                    }`}>
                                        {tItem.published ? t('testimonials_page.admin_badge_published') : t('testimonials_page.admin_badge_pending')}
                                    </span>
                                </div>
                                <div className="flex items-center gap-1 mt-2">
                                    {[1, 2, 3, 4, 5].map((i) => (
                                        <Star
                                            key={i}
                                            className={`w-4 h-4 ${i <= (tItem.rating || 0) ? 'fill-[#FFCC00] text-[#FFCC00]' : 'text-[#E5E7EB]'}`}
                                        />
                                    ))}
                                </div>
                                <p className="text-[#0A0A0A] mt-3 whitespace-pre-line">{tItem.content}</p>
                                <p className="text-xs text-[#9CA3AF] mt-3">
                                    {t('testimonials_page.submitted_on', { date: new Date(tItem.created_at).toLocaleDateString(locale) })}
                                    {tItem.moderated_at && ` · ${t('testimonials_page.admin_moderated_on', { date: new Date(tItem.moderated_at).toLocaleDateString(locale) })}`}
                                </p>
                            </div>
                            <div className="flex flex-col gap-2 shrink-0">
                                {tItem.published ? (
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => moderate(tItem, false)}
                                        disabled={busyId === tItem.id}
                                        data-testid={`unpublish-${tItem.id}`}
                                    >
                                        <EyeOff className="w-4 h-4 mr-1.5" />
                                        {t('testimonials_page.admin_unpublish')}
                                    </Button>
                                ) : (
                                    <Button
                                        size="sm"
                                        onClick={() => moderate(tItem, true)}
                                        disabled={busyId === tItem.id}
                                        className="bg-[#059669] hover:bg-[#047857] text-white"
                                        data-testid={`publish-${tItem.id}`}
                                    >
                                        <Check className="w-4 h-4 mr-1.5" />
                                        {t('testimonials_page.admin_publish')}
                                    </Button>
                                )}
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setConfirmDelete(tItem)}
                                    disabled={busyId === tItem.id}
                                    className="text-[#DC2626] hover:text-[#DC2626]"
                                    data-testid={`delete-${tItem.id}`}
                                >
                                    <Trash2 className="w-4 h-4 mr-1.5" />
                                    {t('testimonials_page.admin_delete')}
                                </Button>
                            </div>
                        </div>
                    </div>
                ))}

                {/* Pagination */}
                <div className="flex items-center justify-between px-4 py-3 border-t border-[#E5E7EB]" data-testid="testimonials-pagination">
                    <p className="text-xs text-[#9CA3AF]">
                        {total === 0
                            ? t('testimonials_page.admin_pagination_none')
                            : t('testimonials_page.admin_pagination_range', {
                                from: page * PAGE_SIZE + 1,
                                to: Math.min((page + 1) * PAGE_SIZE, total),
                                total
                              })}
                    </p>
                    <div className="flex items-center gap-2">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setPage((p) => Math.max(0, p - 1))}
                            disabled={page === 0}
                            data-testid="testimonials-prev"
                        >
                            {t('common.previous')}
                        </Button>
                        <span className="text-xs text-[#4B5563] px-2">{t('testimonials_page.admin_page_n_of', { current: page + 1, total: totalPages })}</span>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setPage((p) => p + 1)}
                            disabled={(page + 1) * PAGE_SIZE >= total}
                            data-testid="testimonials-next"
                        >
                            {t('common.next')}
                        </Button>
                    </div>
                </div>
            </div>

            <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t('testimonials_page.admin_delete_title')}</AlertDialogTitle>
                        <AlertDialogDescription>
                            {t('testimonials_page.admin_delete_description')}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleDelete}
                            className="bg-[#DC2626] hover:bg-[#B91C1C] text-white"
                            data-testid="confirm-delete-testimonial"
                        >
                            {t('common.delete')}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
